import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import PageStats from '@components/Common/PageStats';
import { PageHero } from '@components/Common/Panel';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { csvCell, downloadCsv } from '@components/Referrals/payoutExport';
import { formatDate, formatDateTime } from '@helpers/date';
import {
  SUPPRESSION_CSV_HEADER,
  type EmailSuppressionItem,
  type EmailSuppressionPage,
  suppressionAccountLabel,
  suppressionAccountPath,
  suppressionCsvRow,
  suppressionReasonLabel,
  suppressionReasonTone,
  suppressionsUrl,
} from '@helpers/emailDelivery';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material';
import DeliveryChip from './DeliveryChip';
import axios from 'axios';
import { useEffect, useState } from 'react';
import './index.scss';

export const PAGE_SIZE = 20;
/** Typing pauses this long before the search runs: live like the Send Log, without a request a keystroke. */
export const SEARCH_DEBOUNCE_MS = 300;
/** The BFF's ceiling per request. */
export const EXPORT_PAGE_SIZE = 200;
/** 10,000 addresses. A list longer than that is a question for the database, not a CSV. */
const EXPORT_MAX_PAGES = 50;

/** Every address matching the search, paged through at EXPORT_PAGE_SIZE. */
export const fetchAllSuppressions = async (search: string): Promise<EmailSuppressionItem[]> => {
  const out: EmailSuppressionItem[] = [];
  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const { data } = await axios.get<EmailSuppressionPage>(suppressionsUrl(search, page, EXPORT_PAGE_SIZE));
    const rows = data?.content ?? [];
    out.push(...rows);
    if (rows.length < EXPORT_PAGE_SIZE || out.length >= (data?.totalElements ?? 0)) break;
  }
  return out;
};

export const suppressionsCsv = (rows: EmailSuppressionItem[]): string =>
  [SUPPRESSION_CSV_HEADER, ...rows.map(suppressionCsvRow)].map((line) => line.map(csvCell).join(',')).join('\r\n');

const SELECTORS = ['email', 'reason', 'firstAt', 'lastAt', 'count', 'lastDiagnostic', 'account'];

const HEAD = {
  email: 'Address',
  reason: 'Reason',
  firstAt: 'First blocked',
  lastAt: 'Last event',
  count: 'Events',
  lastDiagnostic: 'Last reason',
  account: 'Account',
};

const BODY = {
  email: (s: EmailSuppressionItem) => <span className="ba-email">{s.email}</span>,
  reason: (s: EmailSuppressionItem) => (
    <DeliveryChip tone={suppressionReasonTone(s.reason)} label={suppressionReasonLabel(s.reason)} />
  ),
  firstAt: (s: EmailSuppressionItem) => <>{formatDate(s.firstAt)}</>,
  lastAt: (s: EmailSuppressionItem) => <>{formatDateTime(s.lastAt)}</>,
  count: (s: EmailSuppressionItem) => <>{(s.count ?? 0).toLocaleString('en-IN')}</>,
  lastDiagnostic: (s: EmailSuppressionItem) =>
    s.lastDiagnostic ? (
      <Tooltip title={s.lastDiagnostic}>
        <span className="ba-diagnostic">{s.lastDiagnostic}</span>
      </Tooltip>
    ) : (
      <>-</>
    ),
  account: (s: EmailSuppressionItem) => {
    const to = suppressionAccountPath(s);
    return to ? <RecordLink to={to}>{suppressionAccountLabel(s.userType)}</RecordLink> : <>-</>;
  },
};

/**
 * Addresses the platform no longer mails: hard bounces, spam complaints, repeated soft bounces,
 * and the addresses imported from Amazon SES's own account-level list.
 *
 * Page state is local, not `?page=`. Every Email Campaigns tab mounts at once, and the Send Log
 * already owns `?page=`; sharing it would page one list whenever the other moved.
 */
const BlockedAddresses: React.FC = () => {
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<EmailSuppressionPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Live search, as on the Send Log: no Apply button, the list follows the field.
  useEffect(() => {
    const q = draft.trim();
    if (q === search) return undefined;
    const t = setTimeout(() => { setSearch(q); setPage(0); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, search]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    axios
      .get<EmailSuppressionPage>(suppressionsUrl(search, page, PAGE_SIZE))
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load blocked addresses'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [search, page, reloadKey]);

  const resetFilters = () => {
    setDraft('');
    setSearch('');
    setPage(0);
  };

  const total = data?.totalElements ?? 0;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllSuppressions(search);
      downloadCsv(`blocked-addresses-${new Date().toISOString().slice(0, 10)}.csv`, suppressionsCsv(rows));
    } catch {
      setError('Could not export blocked addresses');
    } finally {
      setExporting(false);
    }
  };

  /*
   * The Send Log's layout, piece for piece, so the two tabs read as one suite: the page gutter
   * (max-w-container), one outlined panel, the grey filter toolbar with labelled fields and a Reset
   * link, then the count line and the table. This tab had its own Panel + FilterBar strip with
   * Apply/Clear, no gutter and a banded count, and looked like a different product.
   */
  return (
    <Box className="blocked-addresses-page">
      <Box className="ae-page-body max-w-container">
        <PageHero
          kicker="Blocked addresses"
          title="Addresses we no longer email, and why."
          text="A hard bounce or a spam complaint blocks an address at once; soft bounces block it after repeats."
          actions={(
            <Tooltip title="Every page of this view, as a CSV">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={exportCsv}
                  disabled={exporting || total === 0}
                >
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              </span>
            </Tooltip>
          )}
        />

        <Card variant="outlined" className="cc-panel cc-panel--panel">
          <CardContent className="cc-panel__content">
            <Box className="ae-toolbar">
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <FormField
                    required={false}
                    name="blockedAddressSearch"
                    label="Search email address"
                    value={draft}
                    onChange={(_, v) => setDraft(String(v))}
                    placeholder="name@example.com"
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }} className="ba-toolbar-cell">
                  <Button
                    variant="text"
                    onClick={resetFilters}
                    disabled={!draft && !search}
                    size="small"
                    className="ba-reset"
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box className="ae-table-section">
              {/* No count beside an error: "0 addresses" over a failed load read as an empty list. */}
              {!error && data && <PageStats total={total} filtered={data.content?.length ?? 0} label="addresses" />}

              {error && <ErrorMessage message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

              {!error && (loading && !data ? (
                <SkeletonLoader rows={10} columns={SELECTORS.length} />
              ) : (
                <MintlyTable
                  rows={data?.content ?? []}
                  selectors={SELECTORS}
                  tableHeadRow={HEAD}
                  tableBodyRow={BODY}
                  emptyLabel={search ? 'No blocked address matches that search' : 'No blocked addresses'}
                  getRowKey={(s: EmailSuppressionItem) => s.email}
                  columns={{
                    email: { card: 'title', tablet: true },
                    reason: { card: 'status', tablet: true },
                    lastAt: { tablet: true },
                    account: { card: 'fact' },
                  }}
                  pagination={{
                    enabled: true,
                    page,
                    rowsPerPage: PAGE_SIZE,
                    totalNumberOfRows: total,
                    handleChangePage: (_: unknown, p: number) => setPage(p),
                  }}
                />
              ))}

              {!error && data && data.content.length > 0 && (
                <Typography variant="caption" className="ba-footnote">
                  Mail to these addresses is skipped at send time. Amazon SES keeps its own list as well.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BlockedAddresses;
